import Vue from "vue";
import VueRouter, { RouteConfig } from "vue-router";
import { Role } from "@/common/enum";
import { getModule } from "vuex-module-decorators";
import store from "@/store";
import StoreVuexModule from "@/store/StoreVuexModule";
import Shared from "@/common/shared";

Vue.use(VueRouter);

const routes: Array<RouteConfig> = [
  {
    path: "/home",
    name: "Home",
    component: () => import(/* webpackChunkName: "Home" */ "../views/Home.vue"),
    meta: {
      requiresAuth: true,
      permissions: [Role.Owner, Role.Manager, Role.User],
    },
  },
  {
    path: "/accounts",
    name: "Accounts",
    component: () => import(/* webpackChunkName: "Accounts" */ "../views/Accounts.vue"),
    meta: {
      requiresAuth: true,
      permissions: [Role.Owner],
    },
  },
  {
    path: "/users",
    name: "Users",
    component: () => import(/* webpackChunkName: "Users" */ "../views/Users.vue"),
    meta: {
      requiresAuth: true,
      permissions: [Role.Owner, Role.Manager],
    },
  },
  {
    path: "/departments",
    name: "Departments",
    component: () => import(/* webpackChunkName: "Departments" */ "../views/Departments.vue"),
    meta: {
      requiresAuth: true,
      permissions: [Role.Owner],
    },
  },
  {
    path: "/integrations",
    name: "Integrations",
    component: () => import(/* webpackChunkName: "Integrations" */ "../views/IntegrationsView.vue"),
    meta: {
      requiresAuth: true,
      permissions: [Role.Owner],
    },
  },
  {
    path: "/companyreports",
    name: "CompanyReport",
    component: () => import(/* webpackChunkName: "Company" */ "../views/CompanyReport.vue"),
    meta: {
      requiresAuth: true,
      requiredZingRespond: true,
      permissions: [Role.Owner, Role.Manager],
    },
  },
  {
    path: "/workerreports",
    name: "WorkerReport",
    component: () => import(/* webpackChunkName: "Worker" */ "../views/WorkerReport.vue"),
    meta: {
      requiresAuth: true,
      requiredZingRespond: true,
      permissions: [Role.Owner, Role.Manager],
    },
  },
  {
    path: "/tickets",
    name: "Tickets",
    component: () => import(/* webpackChunkName: "Tickets" */ "../views/Tickets.vue"),
    meta: {
      requiresAuth: true,
      permissions: [Role.Owner, Role.Manager, Role.User],
    },
  },
  {
    path: "/tickets/new",
    name: "New-Ticket",
    component: () => import(/* webpackChunkName: "New-Ticket" */ "../views/Ticket.vue"),
    meta: {
      requiresAuth: true,
      permissions: [Role.Owner, Role.Manager, Role.User],
    },
  },
  {
    path: "/tickets/:id",
    name: "View-Ticket",
    component: () => import(/* webpackChunkName: "New-Ticket" */ "../views/Ticket.vue"),
    meta: {
      requiresAuth: true,
      permissions: [Role.Owner, Role.Manager, Role.User],
    },
  },
  {
    path: "/invoices",
    name: "Invoices",
    component: () => import(/* webpackChunkName: "Invoices" */ "../views/Invoices.vue"),
    meta: {
      requiresAuth: true,
      requiresXero: true,
      permissions: [Role.Owner],
    },
  },
  {
    path: "/respond",
    name: "Respond",
    component: () => import(/* webpackChunkName: "Respond" */ "../views/Respond.vue"),
    meta: {
      requiresAuth: true,
      permissions: [Role.Owner],
      requiredZingRespond: true,
      skipRoleIfZing: true,
    },
  },
  {
    path: "/instructions",
    name: "Instructions",
    component: () => import(/* webpackChunkName: "instructions" */ "../views/DownloadInstruction.vue"),
    meta: {
      requiresAuth: true,
      permissions: [Role.Owner, Role.Manager, Role.User],
      requiredZingRespond: true,
    },
  },
  {
    path: "/voicemail",
    name: "Voicemail",
    component: () => import(/* webpackChunkName: "Voicemail" */ "../views/VoiceMails.vue"),
    meta: {
      requiresAuth: true,
      permissions: [Role.Owner],
    },
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes: [
    {
      path: "/",
      component: () => import(/* webpackChunkName: "default-layout" */ "../views/DefaultLayout.vue"),
      children: [
        {
          path: "/",
          redirect: "/home",
        },
        ...routes,
      ],
    },
    {
      path: "/callback",
      component: () => import(/* webpackChunkName: "callback" */ "../views/Callback.vue"),
    },
    {
      path: "/integration-callback",
      component: () => import(/* webpackChunkName: "integration-callback" */ "../views/IntegrationCallback.vue"),
    },
    {
      path: "/login",
      name: "Login",
      component: () => import(/* webpackChunkName: "login" */ "../views/Login.vue"),
    },
    {
      path: "/logout",
      name: "logout",
      component: () => import(/* webpackChunkName: "logout" */ "../views/Logout.vue"),
    },
    {
      path: "/accept-invite",
      name: "AcceptInvite",
      component: () => import(/* webpackChunkName: "accept-invite" */ "../views/AcceptInvite.vue"),
    },
    {
      path: "*",
      redirect: "/404",
    },
    {
      path: "/404",
      name: "404",
      component: () => import(/* webpackChunkName: "404" */ "../views/FileNotFound.vue"),
    },
    {
      path: "/accessdenied",
      name: "403",
      component: () => import(/* webpackChunkName: "403" */ "../views/Forbidden.vue"),
    },
    {
      path: "/offline",
      name: "Offline",
      component: () => import(/* webpackChunkName: "offline" */ "../views/Offline.vue"),
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const requiresXero = to.matched.some((record) => record.meta.requiresXero);
  const parentRoute = to.matched.find((record) => record.meta.permissions);
  const vuexStore = getModule(StoreVuexModule, store);
  const requiredZingRespond = to.matched.some((record) => record.meta.requiredZingRespond);
  const skipRoleIfZing = to.matched.some((record) => record.meta.skipRoleIfZing);

  router.app.$oidc.initialiseManager(vuexStore.environment);

  if (!requiresAuth) {
    return next();
  }

  async function setApiToken() {
    return router.app.$oidc.getAccessToken().then((token) => {
      if (token) {
        vuexStore.setApiToken(token);
      }
      return true;
    });
  }

  router.app.$oidc.getUser(to.path).then(async (user) => {
    if (!user) {
      return next("/login");
    }

    if (!vuexStore.apiToken) {
      await setApiToken();
    }
    try {
      const response: [boolean, number] = await vuexStore.setupUser();

      if (!response[0]) {
        return next("/accessdenied");
      }

      if (response[1] === 404) {
        return next("/404");
      }

      if (response[1] === 401) {
        Shared.showToastMessage(Shared.messages.SESSION_EXPIRED, "is-danger");
        return next("/logout");
      }

      if (response[1] === 403) {
        return next("/accessdenied");
      }

      if (response[1] === 509) {
        return next("/offline");
      }

      if (vuexStore.user) {
        const roleId = vuexStore.user.roleId;
        const hasAccess = parentRoute ? parentRoute.meta.permissions.some((p: number) => p === roleId) : false;

        if (hasAccess || skipRoleIfZing) {
          if (requiresXero && !vuexStore.user.isXeroLinked) {
            return next("/accessdenied");
          }

          if (requiredZingRespond && !vuexStore.user.hasZingRespond) {
            return next("/accessdenied");
          }
          return next();
        }
      }

      return next("/accessdenied");
    } catch (error) {
      return next("/accessdenied");
    }
  });
});

export default router;
